import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToggle } from 'react-use';

import { Button, PageTitle, showNotif, SnackbarAlert } from '@p81-common/ui';
import type { DropDownMenuProps, GridSelectionModel } from '@p81-common/ui';
import { EmptyState, Loading } from 'components/common/Elements/Elements';
import { useRegionPersistedSearch } from 'contexts/SearchPersistenceContext';

import { postRequest } from 'utils/requests';
import { createLinkForRequest } from '../CloudManagementLogs/helpers/helpers';

import {
  ACTIONS_URL_PREFIX,
  ACTIONS_URL_POSTFIX,
  ACTIONS,
  DEFAULT_SELECTION,
  GRID_DETAIL_PANEL_FIELD,
  NETWORK_ROLLOUT_SELECTION_LIMIT,
  defaultModalDetails,
  ModalDetails
} from './helpers/config';
import { useTableSettings } from './helpers/tableSettings';
import { useNetworkRolloutDataGrid } from './helpers/useNetworkRolloutDataGrid';
import ActionCard from './parts/ActionCard';
import CancellationModal from './parts/CancellationModal';
import ConfirmationModal from './parts/ConfirmationModal';
import NetRolloutDataGrid from './parts/NetRolloutDataGrid';
import NetRolloutFilters from './parts/NetRolloutFilters';
import SelectionAlert from './parts/SelectionAlert';
import VersionSelectionModal from './parts/VersionSelectionModal';
import useStyles from './styles';
import { access, hasAccess, resources } from 'utils/permissions';

const networkGwcMap: Record<GridSelectionModel[number], number> = {};

const NetworkRollout = () => {
  const styles = useStyles();
  const { t } = useTranslation(['activity']);

  // Init hooks used by filters and data-grid
  const usedTableSettingsResult = useTableSettings();
  const useDataGridResultProps = useNetworkRolloutDataGrid(
    usedTableSettingsResult.tableConfig
  );
  const { dataConection, loader, results, filter, applySearch } =
    useDataGridResultProps;

  // Keep the typed search when switching DR region (P81-132768).
  useRegionPersistedSearch(filter.search, applySearch);

  // Actions declarations
  const [modalOpen, toggleModalOpen] = useToggle(false);
  const [cancelModalOpen, toggleCancelModalOpen] = useToggle(false);
  const [isLoading, toggleIsLoading] = useToggle(false);
  const [successAlertOpen, toggleSuccessAlertOpen] = useToggle(false);
  const [gradual, setGradual] = useState<boolean | undefined>(undefined);

  const [selectValue, setSelectValue] = useState('');
  const [selectionModel, setSelectionModel] =
    useState<GridSelectionModel>(DEFAULT_SELECTION);
  const [modalDetails, setModalDetails] =
    useState<ModalDetails>(defaultModalDetails);
  const [version, setVersion] = useState<string>('stable');
  const [versionModalOpen, setVersionModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<ACTIONS | null>(null);

  const totalGatewaysSelected = useMemo(
    () =>
      selectionModel.reduce(
        (count: number, id) => count + networkGwcMap[id],
        0
      ),
    [selectionModel]
  );

  // TODO: Implement with real versions
  const [listItems, setListItems] = useState([
    { name: 'Latest', selected: true },
    { name: 'Ver.Y', selected: false },
    { name: 'Ver.X', selected: false }
  ]);
  const handleSelectChange: NonNullable<DropDownMenuProps['handleClick']> =
    useCallback(
      name => () => {
        const selected = selectValue === name ? '' : name.toString();
        setSelectValue(selected);
        setListItems(
          listItems.map(item => ({
            ...item,
            selected: item.name === selected
          }))
        );
      },
      [listItems, selectValue]
    );

  // Actions functions
  const clearSelection = useCallback(
    () => setSelectionModel(DEFAULT_SELECTION),
    []
  );

  const performCalc = useCallback(
    async (action: ACTIONS, v: string, gradual?: boolean) => {
      setGradual(gradual);
      try {
        toggleIsLoading();
        const {
          body: { networksCount: networks = 0, gatewaysCount: gws = 0 } = {}
        } = await postRequest(
          `${ACTIONS_URL_PREFIX}${ACTIONS_URL_POSTFIX.CALC}`,
          {
            action,
            networkIds: selectionModel.length ? selectionModel : '*',
            version: v,
            ...{
              gradual
            }
          }
        );

        if (!(networks || gws)) {
          throw Error('No networks or Gateways found.');
        }

        setModalDetails({ networks, gws, action });
        toggleModalOpen();
        toggleIsLoading();
      } catch (e: any) {
        showNotif(e?.toString(), {
          alertProps: {
            severity: 'error'
          }
        });
      }
    },
    [selectionModel, toggleIsLoading, toggleModalOpen]
  );

  const openVersionModal = useCallback(
    (action: ACTIONS) => () => {
      setPendingAction(action);
      setVersion('stable');
      setVersionModalOpen(true);
    },
    []
  );

  const handleVersionProceed = useCallback(
    (selectedVersion: string, gradual?: boolean) => {
      setVersion(selectedVersion);
      setVersionModalOpen(false);
      if (pendingAction) {
        performCalc(pendingAction, selectedVersion, gradual);
      }
    },
    [pendingAction, performCalc]
  );

  const handleVersionClose = useCallback(() => {
    setVersionModalOpen(false);
    setPendingAction(null);
  }, []);

  const updateRun = useCallback(
    (action: ACTIONS) => async () => {
      toggleIsLoading(true);
      try {
        const { body: { batchId = '' } = {} } = await postRequest(
          `${ACTIONS_URL_PREFIX}${ACTIONS_URL_POSTFIX.RUN}`,
          {
            action,
            networkIds: selectionModel.length ? selectionModel : '*',
            version,
            ...{ gradual }
          }
        );

        setModalDetails({ ...modalDetails, batchId });
        toggleModalOpen();
        toggleSuccessAlertOpen();
        setSelectionModel(DEFAULT_SELECTION);
      } catch (e: any) {
        showNotif(e?.toString(), {
          alertProps: {
            severity: 'error'
          }
        });
      } finally {
        toggleIsLoading(false);
      }
    },
    [
      gradual,
      modalDetails,
      selectionModel,
      toggleIsLoading,
      toggleModalOpen,
      toggleSuccessAlertOpen,
      version
    ]
  );

  const onRowsSelectionHandler = useCallback(
    (ids: GridSelectionModel) => {
      setSelectionModel(ids);
      ids.forEach(id => {
        if (!(id in networkGwcMap)) {
          const gateways = (results?.data.find(row => row._id === id)?.[
            GRID_DETAIL_PANEL_FIELD
          ] || []) as unknown[];
          networkGwcMap[id] = gateways.length;
        }
      });
    },
    [results?.data]
  );

  const handleCloseModal = useCallback(() => {
    toggleModalOpen(false);
    setModalDetails(defaultModalDetails);
    setVersion('stable'); // reset to default when closing
  }, [toggleModalOpen]);

  const handleCloseAllModals = useCallback(() => {
    handleCloseModal();
    toggleCancelModalOpen(false);
  }, [handleCloseModal, toggleCancelModalOpen]);

  const handleSwitchModals = useCallback(() => {
    toggleModalOpen();
    toggleCancelModalOpen();
  }, [toggleCancelModalOpen, toggleModalOpen]);

  const hasSelectionLimit =
    totalGatewaysSelected > NETWORK_ROLLOUT_SELECTION_LIMIT;
  const hasTotalRecordsLimit =
    !selectionModel.length &&
    results &&
    results.totalGwCount > NETWORK_ROLLOUT_SELECTION_LIMIT;

  const disableActions = useMemo(
    () => isLoading || hasSelectionLimit || hasTotalRecordsLimit,
    [hasSelectionLimit, hasTotalRecordsLimit, isLoading]
  );
  const hasNetworkRolloutWriteAccess = hasAccess([
    resources.CS_NETWORK_ROLLOUT,
    access.WRITE
  ]);

  if (loader) {
    return <Loading />;
  }

  if (!dataConection) {
    return <EmptyState message="connect" />;
  }

  return (
    <>
      <PageTitle
        title={t('activity:networkRollout.TITLE')}
        subtitle={t('activity:networkRollout.SUBTITLE')}
        sx={{ marginBottom: 5 }}
      />
      <SnackbarAlert
        open={hasSelectionLimit}
        alertProps={{
          severity: 'error',
          classes: {
            root: styles.errorAlert
          }
        }}
      >
        {t('activity:networkRollout.SNACKBAR_ALERT_SELECTION_LIMIT', {
          selectionLimit: NETWORK_ROLLOUT_SELECTION_LIMIT
        })}
      </SnackbarAlert>
      <SnackbarAlert
        open={successAlertOpen}
        autoHideDuration={10000}
        onClose={toggleSuccessAlertOpen}
        anchorOrigin={{
          horizontal: 'center',
          vertical: 'bottom'
        }}
        alertProps={{
          onClose: toggleSuccessAlertOpen,
          action: (
            <a href={createLinkForRequest({ batchId: [modalDetails.batchId] })}>
              <Button size="small" color="secondary" className={styles.button}>
                {t('activity:networkRollout.SNACKBAR_ALERT_SUCCESS_BUTTON')}
              </Button>
            </a>
          ),
          className: `${styles.alert} ${styles.successAlert}`
        }}
      >
        {t('activity:networkRollout.SNACKBAR_ALERT_SUCCESS_CONTENT', {
          networks: modalDetails.networks,
          gws: modalDetails.gws,
          batchId: modalDetails.batchId
        })}
      </SnackbarAlert>

      <NetRolloutFilters useDataGridResultProps={useDataGridResultProps} />
      {!selectionModel.length && hasNetworkRolloutWriteAccess && (
        <ActionCard
          disabled={disableActions}
          isLoading={isLoading}
          listItems={listItems}
          selectValue={selectValue}
          handleSelectChange={handleSelectChange}
          handleUpdateNetworkClick={openVersionModal(ACTIONS.UPGRADE_NETWORK)}
          handleUpdateServicesClick={openVersionModal(ACTIONS.AUTOMATIC_UPDATE)}
          sx={{ marginBottom: 5 }}
        />
      )}
      <NetRolloutDataGrid
        selectionModel={selectionModel}
        setSelectionModel={onRowsSelectionHandler}
        useDataGridResultProps={useDataGridResultProps}
      />

      <ConfirmationModal
        modalDetails={modalDetails}
        open={modalOpen}
        handleConfirmClick={updateRun(modalDetails.action as ACTIONS)}
        handleCloseClick={handleSwitchModals}
        isLoading={isLoading}
      />
      <VersionSelectionModal
        action={pendingAction}
        open={versionModalOpen}
        initialVersion={version as 'stable' | 'latest'}
        onClose={handleVersionClose}
        onProceed={handleVersionProceed}
      />
      <CancellationModal
        open={cancelModalOpen}
        handleCancelClick={handleCloseAllModals}
        handleCloseClick={handleSwitchModals}
      />
      {selectionModel.length > 0 && (
        <SelectionAlert
          disableActions={disableActions}
          selectionCount={String(totalGatewaysSelected)}
          styles={styles}
          handleClearAllClick={clearSelection}
          handleUpdateNetworkClick={openVersionModal(ACTIONS.UPGRADE_NETWORK)}
          handleUpdateServicesClick={openVersionModal(ACTIONS.AUTOMATIC_UPDATE)}
        />
      )}
    </>
  );
};

export default NetworkRollout;
